import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageType } from 'enums/MessageType';
import { environment } from "environments/environment";
import { DomainAction } from "models/generated/Distribution.Api.Models.AddDomainRequest";
import { DomainRegistrantInfoModel } from "models/generated/Distribution.Api.Models.DomainRegistrantInfo";
import { DomainModel } from 'models/generated/Distribution.Api.Models.Domain';
import { firstValueFrom, Observable } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from './push.service';
import { DomainRenewalInfoModel } from 'models/generated/Distribution.Api.Models.DomainRenewalInfo';
import { DnsRecordsResponseModel } from 'models/generated/Distribution.Api.Models.DnsRecordsResponse';
import { DomainAvailabilityStatus } from 'enums/DomainAvailabilityStatus';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { SiteService } from './site.service';
import { ServerModel } from 'models/generated/Distribution.Api.Models.Server';
import { DnsLookupService } from './dns-lookup.service';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { DomainMoveRequestModel } from 'models/generated/Distribution.Api.Models.DomainMoveRequest';
import { RegistrarManagementRequestDtoModel } from 'models/generated/Distribution.Api.Models.RegistrarManagementRequestDto';
import { SetDomainEmailsRequestModel } from 'models/generated/Distribution.Api.Models.SetDomainEmailsRequest';

export interface DomainWithDnsInformation extends DomainModel {
	server?: string;
	ipAddress?: string;
	nameServer?: string;
	status?: ('not-registrar-managed' | 'is-registrar-managed' | 'awaiting-provider' | 'no-data-validation' | 'awaiting-authid' | 'awaiting-transfer' | 'awaiting-registrar-management-accept')[];
	renewalInfo?: DomainRenewalInfoModel;
	authId?: string;
}

export interface DnsInformationModel {
	name: string;
	ipAddress?: string;
	nameServer?: string;
}

@Injectable()
export class DomainService {
	private baseUrl = `${environment.gatewayUrl}/api/domains`;
	private domainTracker = new LiveDataTracker<DomainModel, { domainId: string, productPlanId?: string, companyId?: string }>(domain => domain.id!);
	private serversPromise: Promise<ServerModel[]>;

	constructor(
		private readonly http: HttpClient,
		private readonly translate: TranslatePipe,
		private readonly dnsLookup: DnsLookupService,
		pushService: PushService,
		siteService: SiteService) {

		this.serversPromise = siteService.getServers();
		pushService.onChanges<{ domainId: string, productPlanId?: string, companyId?: string }>(MessageType.DomainChanges).subscribe(event => {
			const domainId = event.data?.domainId;
			if (domainId) this.domainTracker.refreshData(event, domainId, () => this.getDomainById(domainId), async (domain) => { await this.getDomainStatus(domain, true); });
		});
	}

	getDomainById(id: string): Promise<DomainModel> {
		return firstValueFrom(this.http.get<DomainModel>(`${this.baseUrl}/${id}`));
	}

	getDomainsByName(domainName: string) {
		return this.http.get<DomainModel[]>(`${this.baseUrl}/${domainName}`);
	}

	getDomainByOrderLineId(id: string): Promise<DomainModel> {
		return firstValueFrom(this.http.get<DomainModel>(`${this.baseUrl}/get-by-product-id/${id}`));
	}

	getDomainsByCompanyId(companyId: string, trackingContext?: TrackingContext): Promise<DomainModel[]> {
		return firstValueFrom(this.http.get<DomainModel[]>(`${this.baseUrl}/?companyId=${companyId}`))
			.then(domains => this.domainTracker.trackArrayForComponent(trackingContext, domains, eventData => eventData.companyId == companyId));
	}

	getDomainsNeedingCleanup(): Promise<DomainModel[]> {
		return firstValueFrom(this.http.get<DomainModel[]>(`${this.baseUrl}/?unassigned=true`));
	}

	getDnsRecords(domainName: string): Promise<DnsRecordsResponseModel> {
		return firstValueFrom(this.http.get<DnsRecordsResponseModel>(`${this.baseUrl}/${domainName}/dns-records`));
	}

	getDnsAdminUrl(domainName: string): Promise<string> {
		return firstValueFrom(this.http.get(`${this.baseUrl}/${domainName}/dns-admin-url`, { responseType: 'text' }));
	}

	setDomainEmails(request: SetDomainEmailsRequestModel) {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/set-domain-emails`, request));
	}

	addDomainToCompany(name: string, registrantInfo: DomainRegistrantInfoModel, companyId: string, action?: DomainAction.Nothing | DomainAction.AddDomainForDns): Promise<void> {
		if (!action) action = DomainAction.Nothing;
		return firstValueFrom(this.http.post<void>(this.baseUrl, { action, name, companyId, registrantInfo }));
	}

	addDnsToDomain(domainId: string, registrantInfo: DomainRegistrantInfoModel): Promise<void> {
		return firstValueFrom(this.http.post<void>(this.baseUrl, { action: DomainAction.AddDomainForDns, domainId, registrantInfo }));
	}

	buyDomainForProductPlan(name: string, registrantInfo: DomainRegistrantInfoModel, productPlanId: string, companyId?: string): Promise<void> {
		return firstValueFrom(this.http.post<void>(this.baseUrl, { action: DomainAction.Buy, name, companyId, productPlanId, registrantInfo }));
	}

	transferDomainToRegistrar(domainId: string, registrantInfo: DomainRegistrantInfoModel, authId: string, productVariantId: string, brandId: string, price?: number): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${domainId}/transfer`, { registrantInfo, authId, productVariantId, brandId, price }));
	}

	domainHasPlan(domain: DomainModel): boolean {
		return domain.productPlanId != undefined;
	}

	sendRegistrarManagementRequest(domains: DomainWithDnsInformation[], contact: ContactModel, productVariantId: string, brandId: string, company: CompanyModel) {
		return firstValueFrom(this.http.post(`${this.baseUrl}/request-registrar-management`, {
			domainIds: domains.map(d => d.id!),
			companyId: company.id,
			contactEmail: contact.emailAddress,
			companyName: company.name,
			productVariantId: productVariantId,
			vatNumber: company.vatNumber,
			brandId: brandId
		} as RegistrarManagementRequestDtoModel, { responseType: 'text' }));
	}

	createNewDomainProductPlan(domainId: string, productVariantId: string, brandId: string, price?: number) {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${domainId}/new-product-plan`, { productVariantId, brandId, price }));
	}

	private getCuraNetDomainInformation(domain: DomainModel) {
		if (!domain.metaData) return null;
		return JSON.parse(domain.metaData) as { OrderId?: string, SubscriptionId?: string, DomainDkRegistrarModel?: number, DomainRegistrarAction?: number, AuthId?: string };
	}

	getRenewalInfo(domainName: string): Promise<DomainRenewalInfoModel> {
		return firstValueFrom(this.http.get<DomainRenewalInfoModel>(`${this.baseUrl}/${domainName}/renewal-info`));
	}

	getDomainAvailabilityStatus(domainName: string): Promise<DomainAvailabilityStatus> {
		return firstValueFrom(this.http.get<DomainAvailabilityStatus>(`${this.baseUrl}/${domainName}/availability`));
	}

	async getDnsDetails(domain: DomainWithDnsInformation) {
		const data = await this.dnsLookup.soaLookup(domain.name);
		const servers = await this.serversPromise;

		domain.ipAddress = data.ipAddress;
		domain.nameServer = data.nameServer ?? '';
		if (!data.ipAddress) domain.server = '';
		else {
			const serverName = servers.find(server => server.ip == data.ipAddress)?.name;
			domain.server = serverName ? serverName : this.translate.transform('company.domains.unknown-server', { 'ip': data.ipAddress });
		}
	}

	checkAwaitingDomains(domainNames: string[]): Promise<DomainModel[]> {
		return firstValueFrom(this.http.post<DomainModel[]>(`${this.baseUrl}/check-awaiting-domains`, domainNames));
	}

	resetRegistrarAction(domainId: string): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${domainId}/reset-registrar-action`, null));
	}

	async getDomainStatus(domain: DomainWithDnsInformation, refreshData: boolean = false) {
		const dnsPromise = this.getDnsDetails(domain);
		const metaData = this.getCuraNetDomainInformation(domain);

		if (refreshData || !domain.renewalInfo) domain.renewalInfo = await this.getRenewalInfo(domain.name);
		if (domain.renewalInfo.awaitingProvider) return domain.status = ['awaiting-provider'];

		await dnsPromise;
		if (domain.renewalInfo.registrarAction == 'transfer' && domain.awaitingTaskActionId && (domain.renewalInfo.registrarActionCompleted == false || !this.isOurNameserver(domain.nameServer))) {
			// If there is an uncompleted transfer action, or a completed one where the domain isn't pointing at our nameserver, we're awaiting the transfer to complete (if no authId has been sent, allow that)
			// If productPlanId isn't set, the transfer could be completed, but we are still waiting for Gorm's daily scheduled job to run and update the domain with the new productPlanId, so we also consider that as awaiting transfer
			if (metaData?.AuthId || domain.pendingAuthId || domain.renewalInfo.registrarActionCompleted) return domain.status = ['awaiting-transfer'];
			return domain.status = ['awaiting-authid'];
		}
		
		if (domain.awaitingTaskActionId) {
			// If now awaiting transfer, but still awaiting a Task Action, it means we're awaiting the registrar management request to be accepted
			return domain.status = ['awaiting-registrar-management-accept'];
		}

		domain.status = [];
		if (domain.productPlanId) {
			// Registrar managed domains
			if (!domain.renewalInfo.registrarManaged) domain.status.push('not-registrar-managed');
		}
		else {
			// Registrant managed domains
			if (domain.renewalInfo.registrarManaged) domain.status.push('is-registrar-managed');
		}
		if (domain.renewalInfo.registrantValidated === false) domain.status.push('no-data-validation');
		return domain.status;
	}
	isWarningStatus(status: string) {
		return ['not-registrar-managed', 'is-registrar-managed', 'no-data-validation'].indexOf(status) >= 0;
	}


	removeDomain(domainId: string) {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${domainId}`));
	}

	isOurNameserver(nameServer?: string): boolean {
		const acceptedNameservers = ['ns1.dnsserver.dk', 'ns2.dnsserver.dk'];
		if (!nameServer) return false;
		return acceptedNameservers.includes(nameServer);
	}

	searchDomains(query: string): Observable<DomainModel[]> {
		return this.http.get<DomainModel[]>(`${this.baseUrl}/search`, { params: { query } });
	}

	getDomainAuthId(domainName: string): Promise<string> {
		return firstValueFrom(this.http.get(`${this.baseUrl}/${domainName}/auth-id`, { responseType: 'text' }));
	}

	moveDomainToCompany(domainId: string, moveToCompanyId: string, moveFromVatNumber?: string) {
		let request = new DomainMoveRequestModel();
		request.domainId = domainId;
		request.moveFromVatNumber = moveFromVatNumber;
		request.moveToCompanyId = moveToCompanyId;
		request.include(r => r.moveToRegistrantInfo);
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/move-domain-to-company`, request));
	}

	getRegistrantInfo(companyInfo: CompanyModel) {
		const registrantInfo = new DomainRegistrantInfoModel();
		registrantInfo.vatNumber = companyInfo.vatNumber;
		registrantInfo.companyName = companyInfo.name;
		registrantInfo.address = companyInfo.address;
		registrantInfo.postalCode = companyInfo.zipcode;
		registrantInfo.city = companyInfo.city;
		registrantInfo.countryCode = 'DK';
		registrantInfo.contactName = companyInfo.primaryContact?.fullName ?? '';
		registrantInfo.phone = companyInfo.primaryContact?.contactPhones[0].number ?? '';
		registrantInfo.email = companyInfo.primaryContact?.emailAddress ?? '';
		return registrantInfo;
	}

	submitAuthId(domainId: string, authId: string) {
		return firstValueFrom(this.http.post(`${this.baseUrl}/${domainId}/auth-id`, null, { params: { authId }}));
	}
}
